import {
  AuthCacheManagerBuilder,
  AuthCacheManagerService,
  AuthTokens,
} from './authCacheManager.service';
import {
  AuthHttpError,
  AuthHttpService,
  AuthHttpServiceBuilder,
} from './authHttp.service';
import { AuthStorageService } from './authStorage.service';

export interface AuthClientBuilder {
  signIn(req: { username: string; password: string }): Promise<void>;
  signOut(): Promise<void>;
  getAuthTokens(): ReturnType<AuthCacheManagerBuilder['get']>;
  setUserCache(tokens: AuthTokens): Promise<void>;
  startEventListener(): void;
  dispose(): void;
}

export class AuthClientService implements AuthClientBuilder {
  private readonly requestLock = new Map<
    string,
    ReturnType<AuthClientService['refreshSession']>
  >();

  constructor(
    private httpService: AuthHttpServiceBuilder,
    private cacheManager: AuthCacheManagerBuilder
  ) {}

  async signIn(req: { username: string; password: string }) {
    const tokens = await this.httpService.doFetch<AuthTokens>(`sign-in`, {
      body: JSON.stringify(req),
    });
    await this.cacheManager.set(tokens.body);
  }

  async signOut() {
    await this.cacheManager.clear();
  }

  async getAuthTokens() {
    return this.refreshWithLocking();
  }

  async setUserCache(tokens: AuthTokens) {
    await this.cacheManager.set(tokens);
  }

  startEventListener() {
    this.cacheManager.startEventListener();
  }

  dispose() {
    this.cacheManager.dispose();
  }

  private refreshWithLocking() {
    const cacheKey = 'refresh-lock';
    if (!this.requestLock.has(cacheKey)) {
      const refreshPromise = this.refreshSession().finally(() =>
        this.requestLock.delete(cacheKey)
      );

      this.requestLock.set(cacheKey, refreshPromise);

      return refreshPromise;
    }

    const refreshCachedPromise = this.requestLock.get(cacheKey);
    if (!refreshCachedPromise) {
      throw new AuthHttpError('Fetch promise not found in cache', {
        error: 'CacheError',
        type: 'CacheError',
      });
    }

    return refreshCachedPromise;
  }

  private async refreshSession() {
    const userCache = await this.cacheManager.get();

    const refreshToken = userCache.isExpired && userCache.tokens?.refreshToken;
    if (refreshToken) {
      await this.refreshToken(refreshToken).then(
        (tokens) =>
          tokens?.body &&
          this.cacheManager.set({ ...tokens.body, refreshToken })
      );

      return this.cacheManager.get();
    }

    return userCache;
  }

  private async refreshToken(refreshToken: string) {
    try {
      return await this.httpService.doFetch<AuthTokens>(`refresh-token`, {
        body: JSON.stringify({ refreshToken }),
      });
    } catch (error) {
      if (
        error instanceof AuthHttpError &&
        error.authError.type === 'NetworkError'
      ) {
        return null;
      }

      throw error;
    }
  }
}

export const createAuthClientFactory = (options: { baseUrl: string }) =>
  new AuthClientService(
    new AuthHttpService(window.fetch.bind(window), `${options.baseUrl}/auth`),
    new AuthCacheManagerService(new AuthStorageService())
  );
